import { ChartPlotLine } from '../../../types/ChartPlotLine';
import { EChartsCoreOption } from './echartsCore';
import { AXIS_SPLIT_NUMBER, axisLabelFormat, buildPlotLineSeries, formatAxisLabel, formatAxisTime } from './timeSeriesOption';
import { TOOLTIP_BASE } from './tooltipAdapter';
import { VERTICAL_BAR_TOP_RADIUS } from './barStyle';

export const PLANS_SERIES_NAME = 'Plans';
export const FAILED_PLANS_SERIES_NAME = 'Failed Plans';

export interface PlanPoint {
    x: number;
    y: number;
    plan: any;
    selected: boolean;
}

export interface FailedPlanPoint {
    x: number;
    plan: any;
    message: string;
    selected: boolean;
}

export interface PlanOverlay {
    name: string;
    chartType: 'bar' | 'line';
    color: string;
    data: [number, number][];
    yAxisIndex: number;
}

export interface ExecutionPlanOptionArgs {
    plans: PlanPoint[];
    failedPlans?: FailedPlanPoint[];
    overlays: PlanOverlay[];
    plotLines?: ChartPlotLine[];
    min?: number;
    max?: number;
    timezone: string;
    tooltipFormatter: (params: any) => string;
}

export const SELECTED_COLOUR = '#007bff';
const UNSELECTED_COLOUR = 'rgba(125, 125, 140, 0.7)';
const FAILED_COLOUR = '#dc3545';
const FAILED_SYMBOL = 'path://M2,0 L5,3 L8,0 L10,2 L7,5 L10,8 L8,10 L5,7 L2,10 L0,8 L3,5 L0,2 Z';
const AXIS_COUNT = 4;

const buildPlanSeries = (plans: PlanPoint[]) => ({
    type: 'scatter',
    name: PLANS_SERIES_NAME,
    z: 5,
    cursor: 'pointer',
    data: plans.map(point => ({
        value: [point.x, point.y],
        plan: point.plan,
        symbolSize: point.selected ? 12 : 8,
        itemStyle: {
            color: point.selected ? SELECTED_COLOUR : UNSELECTED_COLOUR,
            borderColor: point.selected ? '#ffffff' : 'transparent',
            borderWidth: point.selected ? 2 : 0,
        },
    })),
});

const buildFailedPlanSeries = (plans: FailedPlanPoint[]) => ({
    type: 'scatter',
    name: FAILED_PLANS_SERIES_NAME,
    z: 5,
    cursor: 'pointer',
    symbol: FAILED_SYMBOL,
    data: plans.map(point => ({
        value: [point.x, 0],
        plan: point.plan,
        message: point.message,
        symbolSize: point.selected ? 14 : 10,
        itemStyle: { color: FAILED_COLOUR },
    })),
});

export const buildExecutionPlanOption = (args: ExecutionPlanOptionArgs): EChartsCoreOption => {
    const plotLines = args.plotLines ?? [];
    const labelFormat = axisLabelFormat((args.max ?? 0) - (args.min ?? 0));

    return {
        animation: false,
        grid: { left: 10, right: 10, top: 18, bottom: args.overlays.length > 0 ? 46 : 8, containLabel: true },
        xAxis: {
            type: 'time',
            containShape: false,
            ...(args.min !== undefined && args.max !== undefined ? { min: args.min, max: args.max } : {}),
            splitNumber: AXIS_SPLIT_NUMBER,
            splitLine: { show: false },
            axisLabel: {
                fontSize: 11,
                margin: 9,
                hideOverlap: true,
                formatter: (value: number) => formatAxisLabel(value, labelFormat, args.timezone),
            },
            axisPointer: {
                type: 'line',
                lineStyle: { width: 15, color: 'rgba(125, 160, 200, 0.15)', type: 'solid' },
            },
        },
        yAxis: Array.from({ length: AXIS_COUNT }, (_, index) => ({
            type: 'value',
            min: 0,
            ...(index > 0 ? { position: 'right', splitLine: { show: false } } : {}),
            axisLabel: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
        })),
        tooltip: {
            ...TOOLTIP_BASE,
            trigger: 'item',
            className: 'execution-plan-point-tooltip',
            formatter: (params: unknown) => args.tooltipFormatter(params),
        },
        legend: {
            show: args.overlays.length > 0,
            type: 'scroll',
            bottom: 0,
            left: 'center',
            data: args.overlays.map(overlay => overlay.name),
            textStyle: { fontSize: 12, fontWeight: 'bold' },
        },
        series: [
            buildPlanSeries(args.plans),
            ...(args.failedPlans && args.failedPlans.length > 0 ? [buildFailedPlanSeries(args.failedPlans)] : []),
            ...args.overlays.map(overlay => ({
                type: overlay.chartType,
                name: overlay.name,
                color: overlay.color,
                yAxisIndex: overlay.yAxisIndex,
                data: overlay.data,
                z: 2,
                ...(overlay.chartType === 'line'
                    ? { showSymbol: false, smooth: false, lineStyle: { width: 1.5 } }
                    : { itemStyle: { borderRadius: VERTICAL_BAR_TOP_RADIUS } }),
            })),
            ...(plotLines.length > 0 ? [buildPlotLineSeries(plotLines)] : []),
        ],
    };
};

export const planTimestamp = (value: number, timezone: string): string =>
    formatAxisTime(value, 'DD MMM YYYY HH:mm', timezone);
